import React, { useRef } from 'react';
import html2pdf from 'html2pdf.js';
import { Button } from './ui/button';
import { FileDown } from 'lucide-react';

/**
 * StudentFormView - Renders a student's assessment form matching the exact template layout.
 * 
 * Props:
 *   formData: The form template (form_data from assessment_forms collection)
 *   studentName: Student's full name
 *   studentId: Student ID string
 *   assessorName: Name of assessor/lecturer
 *   lecturerName: Name of the lecturer
 *   date: Assessment date string
 *   yearOfStudy: Year of study (1, 2, 3)
 *   groupName: Student's global group name
 *   scores: { [criterion_id]: score_number }
 *   sectionComments: { [section_id]: { free_text: string, pills: string[] } }
 *   feedback: Overall feedback string
 *   signatures: { assessor: {signature, signature_type}, student: {signature, signature_type}, ... }
 *   assessmentTitle: Title of the assessment
 *   showExport: Whether to show the Export PDF button (default true)
 */
const StudentFormView = ({
    formData,
    studentName = '',
    studentId = '',
    assessorName = '',
    lecturerName = '',
    date = '',
    yearOfStudy = '',
    groupName = '',
    scores = {},
    sectionComments = {},
    feedback = '',
    signatures = {},
    assessmentTitle = '',
    showExport = true
}) => {
    const printRef = useRef(null);

    if (!formData) return null;

    const sections = formData.sections || [];
    const assessmentScale = formData.assessmentScale || [
        { score: 3, label: 'No evidence' }, { score: 4, label: 'Way below expectations' },
        { score: 5, label: 'Requires more work' }, { score: 6, label: 'Meets standard' },
        { score: 7, label: 'Above standard' }, { score: 8, label: 'Excels' }
    ];
    const scaleScores = assessmentScale.map(s => s.score);
    const extraColumns = formData.extraColumns || [];
    const headerFields = formData.headerFields || {};
    const resultSections = formData.resultSections || {};
    const feedbackSections = formData.feedbackSections || {};
    const signatureFields = formData.signatureFields || [];

    // Calculate totals
    const totalScore = Object.values(scores).reduce((sum, s) => sum + (s || 0), 0);
    const maxScore = sections.reduce((sum, s) => sum + (s.criteria?.length || 0), 0) * Math.max(...scaleScores, 8);

    const exportPDF = async () => {
        if (!printRef.current) return;
        try {
            await html2pdf().set({
                margin: [5, 5, 5, 5],
                filename: `${assessmentTitle || 'Assessment'}_${studentName || 'Student'}.pdf`,
                image: { type: 'jpeg', quality: 0.95 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
            }).from(printRef.current).save();
        } catch (err) {
            console.error('PDF export failed:', err);
        }
    };

    const getCheckMark = (criterionId, score) => {
        return scores[criterionId] === score;
    };

    // Resolve extra column value: extraColumnValues first, then references
    const getExtraColValue = (item, colId) => {
        // Check manually edited values first
        if (item.extraColumnValues?.[colId]) return item.extraColumnValues[colId];
        // Fall back to AI-extracted references
        const refKey = colId === 'qcto_pm' ? 'qcto' : colId === 'highfield_units' ? 'highfield' : colId;
        if (item.references?.[refKey]?.length) return item.references[refKey].join(', ');
        return '';
    };

    return (
        <div className="space-y-3">
            {/* Printable form area */}
            <div ref={printRef} className="bg-white border border-gray-300 mx-auto max-w-[900px] text-[11px] leading-snug" style={{ fontFamily: 'Arial, sans-serif' }} data-testid="student-form-printable">
                {/* Title */}
                <div className="border-b-2 border-black p-4 text-center">
                    <h1 className="text-base font-bold uppercase">{assessmentTitle || formData.description || 'Assessment Form'}</h1>
                </div>

                {/* Header info fields */}
                {(headerFields.unitElo || headerFields.qcto || headerFields.pm || headerFields.highfield || headerFields.cathsseta) && (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 p-3 border-b border-gray-400 text-[10px]">
                        {headerFields.unitElo && <div><span className="font-bold">Unit / ELO / KM:</span> {headerFields.unitElo}</div>}
                        {headerFields.qcto && <div><span className="font-bold">QCTO:</span> {headerFields.qcto}</div>}
                        {headerFields.pm && <div><span className="font-bold">PM:</span> {headerFields.pm}</div>}
                        {headerFields.highfield && <div><span className="font-bold">Highfield Units:</span> {headerFields.highfield}</div>}
                        {headerFields.cathsseta && <div><span className="font-bold">CATHSSETA:</span> {headerFields.cathsseta}</div>}
                    </div>
                )}

                {/* Student info row - AUTO-FILLED */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 p-3 border-b-2 border-black text-[10px]">
                    <div><span className="font-bold">Student Name:</span> {studentName || '___________________'}</div>
                    <div><span className="font-bold">Student Number:</span> {studentId || '___________________'}</div>
                    <div><span className="font-bold">Date:</span> {date || '___________________'}</div>
                    <div><span className="font-bold">Assessor:</span> {assessorName || '___________________'}</div>
                    <div><span className="font-bold">Lecturer:</span> {lecturerName || '___________________'}</div>
                    <div><span className="font-bold">Year of Study:</span> {yearOfStudy || '___________________'}</div>
                    {groupName && <div><span className="font-bold">Group:</span> {groupName}</div>}
                </div>

                {/* Scale legend */}
                <div className="p-2 border-b border-gray-400 bg-gray-50">
                    <span className="font-bold mr-2">Scale:</span>
                    {assessmentScale.map(s => (
                        <span key={s.score} className="mr-3"><span className="font-bold">{s.score}</span> = {s.label || s.description}</span>
                    ))}
                </div>

                {/* Main assessment table */}
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="border-b-2 border-black bg-gray-100">
                            <th className="text-left px-2 py-2 border border-gray-400 font-bold w-[35%]">Task</th>
                            <th className="text-center px-0 py-2 border border-gray-400 font-bold" colSpan={scaleScores.length}>Evaluation</th>
                            <th className="text-left px-2 py-2 border border-gray-400 font-bold w-[20%]">Comments</th>
                            {extraColumns.map(col => (
                                <th key={col.id} className="text-center px-1 py-2 border border-gray-400 font-bold text-[9px] w-[8%]">{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sections.map(section => {
                            const sc = sectionComments[section.id] || {};
                            const commentText = [
                                ...(sc.pills || []),
                                sc.free_text || ''
                            ].filter(Boolean).join('. ');
                            const criteriaRows = section.criteria || [];

                            return (
                                <React.Fragment key={section.id}>
                                    {/* Section header */}
                                    <tr className="bg-[#1e3a5f]">
                                        <td className="px-2 py-2 text-white font-bold border border-gray-600">{section.title}</td>
                                        {scaleScores.map(s => (
                                            <td key={s} className="text-center text-white font-bold border border-gray-600 px-0.5">{s}</td>
                                        ))}
                                        {/* Comments cell - white bg, spans all rows */}
                                        <td className="border border-gray-400 bg-white align-top" rowSpan={criteriaRows.length + 1}>
                                            <div className="px-2 py-1.5 text-[10px] text-gray-800 leading-relaxed">
                                                {(sc.pills || []).length > 0 && (
                                                    <div className="mb-1">
                                                        {sc.pills.map((p, i) => (
                                                            <span key={i} className="inline-block bg-gray-100 text-gray-700 rounded px-1 py-0.5 text-[9px] mr-1 mb-0.5">{p}</span>
                                                        ))}
                                                    </div>
                                                )}
                                                {sc.free_text && <div>{sc.free_text}</div>}
                                            </div>
                                        </td>
                                        {/* Extra columns - per section, spans all rows */}
                                        {extraColumns.map(col => (
                                            <td key={col.id} className="border border-gray-400 bg-white text-center text-[9px] text-gray-800 align-top px-1 py-1.5" rowSpan={criteriaRows.length + 1}>
                                                {getExtraColValue(section, col.id)}
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Criteria rows - no comment or extra column cells (handled by rowSpan) */}
                                    {criteriaRows.map((c, idx) => (
                                        <tr key={c.id} className={idx % 2 === 1 ? 'bg-gray-50' : ''}>
                                            <td className="px-2 py-1.5 border border-gray-300 align-top">
                                                <div className="text-[11px]">{c.label}</div>
                                                {c.guidance && <div className="text-[9px] text-gray-500 italic">{c.guidance}</div>}
                                            </td>
                                            {scaleScores.map(s => (
                                                <td key={s} className="text-center border border-gray-300 px-0 py-1.5 w-[4%]">
                                                    {getCheckMark(c.id, s) ? (
                                                        <div className="w-4 h-4 mx-auto bg-[#1e3a5f] text-white text-[9px] font-bold flex items-center justify-center">✓</div>
                                                    ) : (
                                                        <div className="w-4 h-4 border border-gray-400 mx-auto" />
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>

                {sections.length === 0 && <div className="p-6 text-center text-gray-400 italic">No sections defined</div>}

                {/* Results */}
                {(resultSections.kitchenAssessment || resultSections.tastingAssessment || resultSections.combinedResult) && (
                    <div className="border-t-2 border-black p-3">
                        <p className="font-bold text-[10px] uppercase mb-2">Results</p>
                        <div className="grid grid-cols-3 gap-3">
                            {resultSections.kitchenAssessment && (
                                <div className="border border-gray-400 p-2 text-center">
                                    <p className="font-bold text-[10px]">Kitchen Assessment</p>
                                    <p className="text-[12px] font-bold mt-1">{totalScore} / {maxScore}</p>
                                </div>
                            )}
                            {resultSections.tastingAssessment && (
                                <div className="border border-gray-400 p-2 text-center">
                                    <p className="font-bold text-[10px]">Tasting Assessment</p>
                                    <div className="h-5 border-b border-dotted border-gray-400 mt-1" />
                                </div>
                            )}
                            {resultSections.combinedResult && (
                                <div className="border border-gray-400 p-2 text-center">
                                    <p className="font-bold text-[10px]">Combined Result</p>
                                    <p className="text-[12px] font-bold mt-1">{totalScore} / {maxScore}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Feedback */}
                {(feedbackSections.assessorFeedback || feedbackSections.studentFeedback || feedbackSections.actionPlan) && (
                    <div className="border-t border-gray-400 p-3 space-y-2">
                        {feedbackSections.assessorFeedback && (
                            <div>
                                <p className="font-bold text-[10px]">Assessor Feedback:</p>
                                <div className="border border-gray-300 min-h-[40px] mt-1 p-1 text-[10px]">{feedback || ''}</div>
                            </div>
                        )}
                        {feedbackSections.studentFeedback && (
                            <div>
                                <p className="font-bold text-[10px]">Student Feedback:</p>
                                <div className="border border-gray-300 min-h-[40px] mt-1 p-1 text-[10px]"></div>
                            </div>
                        )}
                        {feedbackSections.actionPlan && (
                            <div>
                                <p className="font-bold text-[10px]">Action Plan:</p>
                                <div className="border border-gray-300 min-h-[40px] mt-1 p-1 text-[10px]"></div>
                            </div>
                        )}
                    </div>
                )}

                {/* Signatures */}
                {signatureFields.length > 0 && (
                    <div className="border-t-2 border-black p-3">
                        <div className={`grid gap-6 ${signatureFields.length > 2 ? 'grid-cols-3' : `grid-cols-${signatureFields.length}`}`}>
                            {signatureFields.map(s => {
                                const sig = signatures[s];
                                const label = s === 'internalVerifier' ? 'Internal Verifier' : s.charAt(0).toUpperCase() + s.slice(1);
                                return (
                                    <div key={s} className="text-center">
                                        <div className="border-b-2 border-black h-10 mb-1 flex items-end justify-center">
                                            {sig ? (
                                                sig.signature_type === 'drawn' ? (
                                                    <img src={sig.signature} alt={`${label} signature`} className="h-8 object-contain" />
                                                ) : (
                                                    <p className="text-sm italic font-serif text-gray-700 pb-0.5">{sig.signature}</p>
                                                )
                                            ) : null}
                                        </div>
                                        <p className="text-[10px] font-bold">{label}</p>
                                        <p className="text-[9px] text-gray-500 mt-0.5">Date: {date || '____________'}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Export button */}
            {showExport && (
                <div className="flex justify-end">
                    <Button variant="outline" onClick={exportPDF} data-testid="student-form-export-pdf">
                        <FileDown className="w-4 h-4 mr-1" /> Export PDF
                    </Button>
                </div>
            )}
        </div>
    );
};

export default StudentFormView;
